import { environment } from 'environments/environment';
import { buildPostHogConfig } from './posthog.config';
import { montarPropriedadesGlobais } from './posthog-globals';
import { detectarContextoEmbed } from './posthog-embed';
import { limparCookiePostHogLegado } from './posthog-cookie-cleanup';
import type { PostHog } from 'posthog-js';

let cliente: PostHog | null = null;
let carregamento: Promise<void> | null = null;

/**
 * Carrega e configura o posthog-js uma única vez por carga de página.
 *
 * O estado vive no módulo, e não numa instância injetável, porque cada montagem de
 * folha Angular cria uma plataforma e um injector próprios: uma guarda de instância é
 * reconstruída junto com o serviço e deixaria o SDK — que é singleton do realm do
 * browser — ser reinicializado a cada navegação que remonta a folha.
 *
 * A promessa em andamento é memoizada para que chamadas concorrentes (o initializer
 * do shell e o de uma folha montando em seguida) compartilhem o mesmo carregamento em
 * vez de disputarem dois posthog.init().
 */
export function iniciarClientePostHog(): Promise<void> {
  if (!carregamento) {
    carregamento = carregarCliente();
  }
  return carregamento;
}

/** Instância ativa do SDK, ou null quando desligado por ambiente ou com carregamento falho. */
export function obterClientePostHog(): PostHog | null {
  return cliente;
}

async function carregarCliente(): Promise<void> {
  const enabled = environment.posthogEnabled;
  const apiKey = environment.posthogApiKey;
  const apiHost = environment.posthogHost;

  if (!enabled || !apiKey) {
    return;
  }

  limparCookiePostHogLegado(apiKey);

  try {
    const mod = await import('posthog-js');
    // O pacote expõe a mesma instância como default e como named export; qual das duas
    // chega depende do build resolvido (ESM no webpack, CJS no Jest).
    const posthog: PostHog = mod.default ?? mod.posthog;

    if (!posthog || typeof posthog.init !== 'function') {
      throw new Error('PostHog library could not be resolved from import');
    }


    const contextoEmbed = detectarContextoEmbed();

    posthog.init(
      apiKey,
      buildPostHogConfig({
        apiHost,
        sessionReplayEnabled: environment.posthogSessionReplayEnabled,
        // Único gate de surveys que vive em código: contexto de iframe (SEG-2834).
        // Não é expressável no PostHog — dentro do iframe a URL do center-web é a
        // mesma e não há diferença de DOM. A separação de ambiente ficou nas
        // conditions.url das surveys, e o público no targeting por user_role.
        surveysEnabled: !contextoEmbed.embedded,
        onLoaded: () => aplicarEstadoInicial(posthog)
      })
    );

    cliente = posthog;
  } catch (err) {
    console.warn('[PostHogService] init falhou', err);
    cliente = null;
  }
}

/**
 * Indica se o SDK está ativo e sem identidade aplicada.
 *
 * Resetar uma pessoa anônima não descarta identidade nenhuma: só rotaciona o id e a sessão
 * anônimos (deixando os anteriores órfãos, o que quebra o funil da tela de login) e dispara
 * um novo /flags. Por isso o reset e o evento de logout são dispensados nesse estado.
 *
 * Falha segura: sem cliente, sem o método (uma versão futura do SDK pode removê-lo, já que
 * é de uso interno) ou com o método lançando, devolve false e o comportamento é o de sempre
 * — o reset e o evento acontecem.
 */
export function clienteAnonimo(): boolean {
  const posthog = cliente;
  if (!posthog || typeof posthog._isIdentified !== 'function') {
    return false;
  }
  try {
    return posthog._isIdentified() === false;
  } catch {
    return false;
  }
}

/**
 * Reseta o SDK e reaplica as propriedades globais e o ambiente, que o reset apaga junto com
 * o resto da persistência. Sem as globais, os eventos pós-logout perdem app_name, ambiente,
 * versão e contexto.
 * Não faz nada com o SDK já anônimo: sem reset, o que foi registrado continua valendo.
 * Nunca lança; sem cliente ativo não faz nada.
 */
export function resetarClientePostHog(): void {
  const posthog = cliente;
  if (!posthog || clienteAnonimo()) {
    return;
  }
  executarIsolado('reset', () => posthog.reset());
  executarIsolado('propriedades globais', () => posthog.register(montarPropriedadesGlobais()));
  aplicarAmbiente(posthog);
}

// Cada passo é isolado: o SDK aborta o restante do callback na primeira exceção, e um
// descarte de override com defeito não pode deixar o ambiente sem registro.
function aplicarEstadoInicial(posthog: PostHog): void {
  executarIsolado('descarte de override', () => descartarOverridesPersistidos(posthog));
  executarIsolado('propriedades globais', () => posthog.register(montarPropriedadesGlobais()));
  aplicarAmbiente(posthog);
}

// Decidido só por environment.production: um override gravado no localStorage de quem
// está sendo afetado por um incidente seguraria o rollback.
function descartarOverridesPersistidos(posthog: PostHog): void {
  if (!environment.production) {
    return;
  }
  posthog.featureFlags?.overrideFeatureFlags(false);
}

// Sem recarregar: no init o primeiro /flags já é garantido pelo carregamento da configuração
// remota, e no reset pelo debouncer que o próprio reset armou. A propriedade de pessoa é a
// única forma de o ambiente chegar às condições de release; a super property de evento
// registrada nos globais não entra na avaliação.
function aplicarAmbiente(posthog: PostHog): void {
  executarIsolado('aplicação do ambiente', () =>
    posthog.setPersonPropertiesForFlags({ ambiente: environment.ambiente }, false)
  );
}

function executarIsolado(etapa: string, acao: () => void): void {
  try {
    acao();
  } catch (err) {
    console.warn(`[PostHogService] ${etapa} falhou`, err);
  }
}

/** Descarta o estado de módulo entre testes. Não usar em código de produção. */
export function resetClientePostHogParaTestes(): void {
  cliente = null;
  carregamento = null;
}
